import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../style/Admin.css';

const API = process.env.REACT_APP_API_URL || '';

const EMPTY_FORM = {
    title:           '',
    jobCode:         '',
    departmentId:    1,
    location:        '',
    experienceLevel: 'Mid-Level',
    employmentType:  'Full-Time',
    salaryRange:     '',
    description:     '',
    status:          'ACTIVE'
};

export default function AdminJobForm() {
    const [form, setForm]       = useState(EMPTY_FORM);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState('');
    const { token, logout }     = useAdminAuth();
    const navigate              = useNavigate();
    const { id }                = useParams();
    const isEdit                = !!id;

    useEffect(() => {
        if (!isEdit) return;
        fetch(`${API}/api/admin/jobs/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
            if (res.status === 401) { logout(); navigate('/admin/login'); return null; }
            return res.json();
        })
        .then(data => { if (data) setForm(data); })
        .catch(() => setError('Failed to load job.'));
    }, [id]); // eslint-disable-line

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const url    = isEdit ? `${API}/api/admin/jobs/${id}` : `${API}/api/admin/jobs`;
            const method = isEdit ? 'PUT' : 'POST';
            const res    = await fetch(url, {
                method,
                headers: {
                    'Content-Type':  'application/json',
                    Authorization:   `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) {
                navigate('/admin/dashboard');
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to save job.');
            }
        } catch {
            setError('Cannot connect to server.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-layout">
            <header className="admin-header">
                <div className="admin-header-left">
                    <span className="admin-header-logo">KOVAN Admin</span>
                </div>
                <div className="admin-header-right">
                    <button className="admin-btn-outline" onClick={() => navigate('/admin/dashboard')}>
                        ← Back to Dashboard
                    </button>
                </div>
            </header>

            <main className="admin-main">
                <h1 className="admin-page-title">{isEdit ? 'Edit Job' : 'Post New Job'}</h1>

                {error && <div className="admin-error">{error}</div>}

                <form onSubmit={handleSubmit} className="admin-job-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <label>Job Title *</label>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                placeholder="e.g. Software Engineer"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Job Code</label>
                            <input
                                name="jobCode"
                                value={form.jobCode}
                                onChange={handleChange}
                                placeholder="e.g. JOB-001"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Location *</label>
                            <input
                                name="location"
                                value={form.location}
                                onChange={handleChange}
                                placeholder="e.g. Kuala Lumpur / Remote"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Salary Range</label>
                            <input
                                name="salaryRange"
                                value={form.salaryRange}
                                onChange={handleChange}
                                placeholder="e.g. RM 5,000 – RM 8,000"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Experience Level *</label>
                            <select name="experienceLevel" value={form.experienceLevel} onChange={handleChange} required>
                                <option>Entry-Level</option>
                                <option>Mid-Level</option>
                                <option>Senior</option>
                                <option>Lead</option>
                                <option>Manager</option>
                            </select>
                        </div>

                        <div className="admin-form-group">
                            <label>Employment Type *</label>
                            <select name="employmentType" value={form.employmentType} onChange={handleChange} required>
                                <option>Full-Time</option>
                                <option>Part-Time</option>
                                <option>Contract</option>
                                <option>Internship</option>
                            </select>
                        </div>

                        <div className="admin-form-group">
                            <label>Status *</label>
                            <select name="status" value={form.status} onChange={handleChange} required>
                                <option value="ACTIVE">Active (visible on Careers page)</option>
                                <option value="DRAFT">Draft (hidden from public)</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-form-group admin-form-full">
                        <label>Job Description *</label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Describe the role, responsibilities, requirements..."
                            rows={10}
                            required
                        />
                    </div>

                    <div className="admin-form-actions">
                        <button type="submit" className="admin-btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Post Job')}
                        </button>
                        <button
                            type="button"
                            className="admin-btn-outline"
                            onClick={() => navigate('/admin/dashboard')}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
